import {
  HeaderLogo,
  hasThemeSwitcher,
  headerLogoSize,
} from '@asset/themes/config';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import { useDebouncedCallback } from 'use-debounce';
import { useForm } from 'react-hook-form';
import { useHelper } from 'src/hooks';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import { web3 } from '@state/web3';
import DarkMode from './ThemeSwitcher';
import NetworkSwitcher from './NetworkSwitcher';
import SkeletonImage from '@com/utils/SkeletonImage';
import styled from '@emotion/styled';

const MobileItem = styled.li`
  display: none;
  @media (max-width: 991px) {
    display: block;
  }
`;

const SearchForm = styled.div`
  position: absolute;
  top: 50%;
  -webkit-transform: translateY(-50%);
  -ms-transform: translateY(-50%);
  -o-transform: translateY(-50%);
  transform: translateY(-50%);
  left: 12%;
  width: 24%;

  @media (max-width: 1440px) {
    display: none;
  }

  @media (max-width: 1800px) {
    left: 15%;
    width: 16%;
  }
`;

const SearchFormContainer = styled.form`
  position: relative;
  width: 100%;

  input {
    padding: 12px 18px 12px 48px;
    background-color: transparent;
    border-radius: 24px;
    border: 1px solid var(--primary-color9);
    outline: none;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.5px;

    ::placeholder {
      font-size: 14px;
      line-height: 20px;
    }
  }

  div {
    display: flex;
    position: absolute;
    width: 64px;
    height: 100%;
    top: 0;
    justify-content: center;
    align-items: center;
  }

  button {
    border-radius: 12;
    padding: 0;
    border: none;
    background-color: transparent;
    padding: 14px 0;
  }
`;

const MobileMenuNetworkSwitcher = styled.li`
  padding: 10px 10px !important;
  display: none;

  @media (max-width: 620px) {
    display: block;
  }
`;

const DropdownMenuContainer = styled.li`
  @media (max-width: 1330px) {
    border-radius: 10px !important;
    padding: 0 !important;
    width: 48px;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const DropdownMenuName = styled.span`
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-left: 1.5rem !important;
  line-height: 32px !important;
  @media (max-width: 1330px) {
    display: none !important;
  }
`;

const DropdownMenu = styled.ul`
  margin-top: 1.5em !important;
  @media (max-width: 991px) {
    display: none;
  }
`;

const DropdownMenuItem = styled.li`
  /*  */
`;

function Header(): JSX.Element {
  /* Define state / hook */
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { disconnect } = web3.useContainer();
  const { userData, isLogin } = user.useContainer();
  const headerRef = useRef(null);
  const menuLeft = useRef<any>(null);
  const btnToggle = useRef<any>(null);
  const navigate = useNavigate();
  const { displayName } = useHelper();
  const { register: registerField, handleSubmit } = useForm<{
    search: string;
  }>();
  const { register: registerMenuField, handleSubmit: handleMenuSubmit } =
    useForm<{
      search: string;
    }>();

  /* Custom Data Object for render or other usages */

  /* Render control function / logic */
  const menuToggle = useDebouncedCallback(
    () => {
      menuLeft.current.classList.toggle('active');
      btnToggle.current.classList.toggle('active');
    },
    400,
    { leading: true, trailing: false }
  );

  const searchRedirect = (formData: { search: string }) => {
    navigate({
      pathname: '/search/collection',
      search: `?s=${encodeURIComponent(formData.search)}`,
    });
  };

  /* Data Change Listeners - useEffect/useMemo/useCallback */
  const menus = [
    {
      id: 1,
      name: `${t('header.home')}`,
      links: '/',
      testLabel: 'header-home-btn',
    },
    {
      id: 2,
      name: `${t('header.collection')}`,
      links: '/collection',
      testLabel: 'header-collection-btn',
    },
    {
      id: 3,
      name: `${t('header.launchpad')}`,
      links: '/launchpad',
      testLabel: 'header-launchpad-btn',
    },
    {
      id: 4,
      name: t('header.faq'),
      links: '/faq',
      testLabel: 'header-faq-btn',
    },
  ];

  /* Styling */
  const HeaderNetworkSwitcher = useMemo(
    () => styled.div`
      margin-right: 2rem;
      @media (max-width: 991px) {
        margin-right: ${isLogin ? '8rem' : '2rem'};
        margin-top: 0.4rem;
      }

      @media (max-width: 620px) {
        display: none;
      }
    `,
    [isLogin]
  );

  /* Render */
  return (
    <header
      ref={headerRef}
      className="header_1 header_2 style2 js-header"
      id="header_main"
    >
      <div className="themesflat-container">
        <div id="site-header-inner">
          <div className="wrap-box flex">
            <div className="clearfix" id="site-logo">
              <div id="site-logo-inner">
                <Link className="main-logo" rel="home" to="/">
                  <img
                    alt="Header Logo"
                    className="ml-2 rounded-0"
                    id="logo_header"
                    loading="lazy"
                    src={HeaderLogo}
                    width={headerLogoSize}
                  />
                </Link>
              </div>
            </div>
            <div ref={btnToggle} className="mobile-button" onClick={menuToggle}>
              <span />
            </div>
            <SearchForm>
              <SearchFormContainer
                className="header-search-form-container"
                onSubmit={handleSubmit(searchRedirect)}
              >
                <div>
                  <button type="submit">
                    <i className="far fa-search" />
                  </button>
                </div>
                <input
                  required
                  maxLength={50}
                  minLength={3}
                  pattern="^(?!\s)(?!.*\s$).{3,}$"
                  placeholder={t('header.searchPlaceholder')}
                  type="search"
                  {...registerField('search')}
                />
              </SearchFormContainer>
            </SearchForm>
            <nav
              ref={menuLeft}
              className="main-nav"
              css={css`
                left: 38% !important;
                @media (max-width: 1800px) {
                  left: 32% !important;
                }

                @media (max-width: 1440px) {
                  left: 20% !important;
                }

                @media (max-width: 991px) {
                  left: 0 !important;
                }
              `}
              id="main-nav"
            >
              <ul className="menu" id="menu-primary-menu">
                <MobileItem>
                  <SearchFormContainer
                    className="header-search-form-container p-2"
                    onSubmit={handleMenuSubmit(searchRedirect)}
                  >
                    <div>
                      <button type="submit">
                        <i className="far fa-search" />
                      </button>
                    </div>
                    <input
                      required
                      maxLength={50}
                      minLength={3}
                      pattern="^(?!\s)(?!.*\s$).{3,}$"
                      placeholder={t('header.searchPlaceholder')}
                      type="search"
                      {...registerMenuField('search')}
                    />
                  </SearchFormContainer>
                </MobileItem>
                <MobileMenuNetworkSwitcher>
                  <NetworkSwitcher />
                </MobileMenuNetworkSwitcher>

                {isLogin ? (
                  <>
                    <MobileItem>
                      <div className="fs-16 p-3 ml-2 d-flex align-items-center flex-nowrap">
                        <SkeletonImage
                          alt="Avatar"
                          className="rounded mr-3 w-100 h-100"
                          height={32}
                          ratio={1}
                          size={[64, 128]}
                          src={userData?.user?.avatar}
                          width={32}
                        />
                        <div className="text-truncate flex-grow-1">
                          {userData?.user?.name}
                        </div>
                      </div>
                    </MobileItem>
                    <MobileItem>
                      <Link
                        css={
                          pathname === '/profile/my'
                            ? css`
                                text-decoration: underline 0.1em;
                                text-underline-offset: 0.3em;
                                :hover {
                                  text-decoration: underline 0.1em;
                                }
                              `
                            : ''
                        }
                        to="/profile/my"
                      >
                        {t('header.profile')}
                      </Link>
                    </MobileItem>
                    <MobileItem>
                      <a
                        href={`${process.env.REACT_APP_API_BASE_URL}/creator`}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {t('header.creatorPanel')}
                      </a>
                    </MobileItem>
                  </>
                ) : (
                  <MobileItem>
                    <Link
                      css={
                        pathname === '/login'
                          ? css`
                              text-decoration: underline 0.1em;
                              text-underline-offset: 0.3em;
                              :hover {
                                text-decoration: underline 0.1em;
                              }
                            `
                          : ''
                      }
                      to="/login"
                    >
                      {t('header.login')}
                    </Link>
                  </MobileItem>
                )}
                {menus.map((item, index) => (
                  <li key={index} data-test={item.testLabel}>
                    <Link
                      css={
                        pathname === item.links
                          ? css`
                              text-decoration: underline 0.1em;
                              text-underline-offset: 0.3em;
                              :hover {
                                text-decoration: underline 0.1em;
                              }
                            `
                          : ''
                      }
                      to={item?.links || '#'}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
                <li data-test="header-about">
                  <a href={`${process.env.REACT_APP_API_BASE_URL}/about`}>
                    {t('header.about')}
                  </a>
                </li>
                {isLogin && (
                  <MobileItem>
                    <Link to="#" onClick={() => disconnect()}>
                      {t('header.logout')}
                    </Link>
                  </MobileItem>
                )}
              </ul>
            </nav>
            <div
              className="flat-search-btn flex"
              css={css`
                @media (min-width: 1330px) {
                  padding: 10px 0;
                }
              `}
            >
              <div className="sc-btn-top mg-r-12" id="site-header">
                <div className="row d-flex align-items-center">
                  <HeaderNetworkSwitcher>
                    <NetworkSwitcher />
                  </HeaderNetworkSwitcher>
                  {userData ? (
                    <div
                      className="main-nav"
                      css={css`
                        position: relative;
                        left: 0;
                        @media (max-width: 991px) {
                          display: none;
                        }
                      `}
                    >
                      <DropdownMenuContainer className="sc-button header-slider style style-1 fl-button pri-1 flex">
                        <SkeletonImage
                          alt="Avatar"
                          className="rounded"
                          height={32}
                          ratio={1}
                          size={[64, 128]}
                          src={userData?.user?.avatar}
                          width={32}
                        />
                        <DropdownMenuName>
                          {displayName(userData?.user?.name)}
                        </DropdownMenuName>
                        <DropdownMenu className="sub-menu">
                          <DropdownMenuItem className="menu-item">
                            <Link to="/profile/my">{t('header.profile')}</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem className="menu-item">
                            <a
                              href={`${process.env.REACT_APP_API_BASE_URL}/creator`}
                              rel="noreferrer"
                              target="_blank"
                            >
                              {t('header.creatorPanel')}
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="menu-item"
                            onClick={async () => {
                              await disconnect();
                            }}
                          >
                            <Link to="/">{t('header.logout')}</Link>
                          </DropdownMenuItem>
                        </DropdownMenu>
                      </DropdownMenuContainer>
                    </div>
                  ) : (
                    <button
                      className="sc-button header-slider style style-1 wallet fl-button pri-1"
                      css={css`
                        @media (max-width: 991px) {
                          margin-right: ${hasThemeSwitcher ? '65px' : '15px'};
                        }

                        @media (max-width: 1200px) {
                          padding: 1rem 1rem !important;
                        }
                      `}
                      type="button"
                      onClick={() => navigate('/login')}
                    >
                      <span>{t('header.login')}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DarkMode />
    </header>
  );
}

export default Header;
